import classNames from 'classnames';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useMatchMedia from 'hooks/useMatchMedia';
import { DropdownOption } from 'models/Dropdown';
import { INITIAL_POSITION } from 'data/global';

import './RightClickDropdown.styles.scss';

type RightClickDropdownProps = {
  className?: string;
  disabled?: boolean;
  children?: ReactNode;
  options: DropdownOption[];
};

const RightClickDropdown: FC<RightClickDropdownProps> = ({
  className,
  disabled,
  options,
  children,
}) => {
  const classes = classNames('right-click-dropdown', className);

  const containerRef = useRef<HTMLDivElement>();
  const contentRef = useRef<HTMLUListElement>();

  const isPhablet = useMatchMedia('isPhablet');

  const [isOpen, setIsOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] =
    useState(INITIAL_POSITION);

  const handleRightClick = useCallback(
    (e) => {
      e.preventDefault();

      if (disabled) return;

      const posX = e.clientX; // X-coordinate of the right-click
      const posY = e.clientY; // Y-coordinate of the right-click
      const menuWidth = contentRef.current.clientWidth;
      const menuHeight = contentRef.current.clientHeight;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      let left = posX;
      let top = posY;

      // Check if there is not enough space to render the context menu to the right
      if (windowWidth - posX < menuWidth) {
        left = posX - menuWidth; // Render the context menu to the left of the right-click
      }

      // If there is no space below, render above
      if (windowHeight - posY < menuHeight) {
        top = posY - menuHeight; // Render the context menu above the right-click
      }

      setContextMenuPosition({ top, left });
      setIsOpen(true);
    },
    [disabled],
  );

  const dropdownOptions = useMemo(
    () => (
      <ul
        ref={contentRef}
        style={contextMenuPosition}
        onContextMenu={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={classNames('right-click-dropdown-menu', {
          'right-click-dropdown-menu--open': isOpen,
        })}
      >
        {options.map(
          ({ onClick, label, icon, alert, disabled, className }, index) => (
            <li
              key={index}
              className={classNames({ delete: alert, disabled, className })}
              onContextMenu={(e) => e.stopPropagation()}
              onClick={(e) => {
                onClick?.(e);
                setIsOpen(false);
              }}
            >
              {icon}
              {label}
            </li>
          ),
        )}
      </ul>
    ),
    [contextMenuPosition, isOpen, options],
  );

  useEffect(() => {
    const handleRightClickedOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (
        !containerRef.current?.contains(target) &&
        !contentRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    window.addEventListener('contextmenu', handleRightClickedOutside);

    return () =>
      window.removeEventListener('contextmenu', handleRightClickedOutside);
  }, []);

  useEffect(() => {
    const handleClickedOutside = (event: MouseEvent) => {
      if (!contentRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    window.addEventListener('click', handleClickedOutside);

    return () => window.removeEventListener('click', handleClickedOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsOpen(false);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isPhablet) return <>{children}</>;

  return (
    <div
      className={classes}
      onContextMenu={handleRightClick}
      ref={containerRef}
    >
      {children}

      {!!options.length && dropdownOptions}
    </div>
  );
};

export default RightClickDropdown;
